import {app, HostClientType} from '@microsoft/teams-js';
import {createContext, useContext, useEffect, useState} from 'react';
import {Container, Spinner} from 'reactstrap';
import {getShellName} from '../lib/useShellSession';

export interface TeamsState {
    isRunningInTeams?: boolean;
    /**
     * False on Teams hosts that cannot hand a tab a live microphone stream.
     * Teams mobile only exposes the microphone through media.selectMedia,
     * which records a clip, so realtime voice cannot run there. Desktop and
     * web allow getUserMedia once the manifest declares devicePermissions
     * "media". Always true outside Teams.
     */
    supportsLiveMicrophone: boolean;
}

// Hosts where getUserMedia works inside a tab. An unknown client type is
// treated as capable so a Teams release that adds one does not hide voice.
const liveMicrophoneHostClientTypes = new Set<string>([
    HostClientType.desktop,
    HostClientType.web,
    HostClientType.macos
]);

const hostSupportsLiveMicrophone = (clientType: string | undefined) =>
    !clientType ||
    liveMicrophoneHostClientTypes.has(clientType) ||
    !Object.values(HostClientType).includes(clientType as HostClientType);

export const TeamsContext = createContext<TeamsState | undefined>(undefined);

const TIMEOUT_SECONDS = 10;

const TeamsLoadingScreen = () => {
    const isDark = localStorage.getItem('theme') === 'dark';
    const logoSrc = isDark
        ? '/images/logo-dark-mode.svg'
        : '/images/logo-light-mode.svg';

    const [countdown, setCountdown] = useState(TIMEOUT_SECONDS);

    useEffect(() => {
        if (countdown <= 0) return;
        const id = setTimeout(() => setCountdown(c => c - 1), 1000);
        return () => clearTimeout(id);
    }, [countdown]);

    return (
        <Container>
            <div
                className="d-flex flex-column align-items-center justify-content-center"
                style={{minHeight: '100vh', gap: '2rem'}}
            >
                <img width={200} src={logoSrc} alt="Logo" />
                <Spinner color="primary" />
                <p className="text-muted mb-0">
                    Checking if running in Microsoft Teams&hellip; {countdown}
                </p>
            </div>
        </Container>
    );
};

export const TeamsProvider: React.FC<{children: React.ReactNode}> = ({
    children
}) => {
    const [teamsLoading, setTeamsLoading] = useState(true);
    const [isRunningInTeams, setIsRunningInTeams] = useState<
        boolean | undefined
    >(undefined);
    const [supportsLiveMicrophone, setSupportsLiveMicrophone] = useState(true);

    useEffect(() => {
        if (teamsLoading) {
            if (window.location.hostname === 'demo4.thetaassist.ai') {
                setIsRunningInTeams(false);
                setTeamsLoading(false);
                return;
            }
            if (getShellName()) {
                setIsRunningInTeams(false);
                setTeamsLoading(false);
                return;
            }
            const timeout = new Promise<never>((_, reject) =>
                setTimeout(
                    () => reject(new Error('timeout')),
                    TIMEOUT_SECONDS * 1000
                )
            );
            Promise.race([app.initialize(), timeout])
                .then(async () => {
                    try {
                        const context = await app.getContext();
                        setSupportsLiveMicrophone(
                            hostSupportsLiveMicrophone(
                                context.app.host.clientType
                            )
                        );
                    } catch {
                        // Keep the default: voice stays available.
                    }
                    setIsRunningInTeams(true);
                    setTeamsLoading(false);
                })
                .catch(() => {
                    setIsRunningInTeams(false);
                    setTeamsLoading(false);
                });
        }
    }, [teamsLoading]);

    if (teamsLoading || isRunningInTeams === undefined) {
        return <TeamsLoadingScreen />;
    }

    return (
        <TeamsContext.Provider
            value={{isRunningInTeams, supportsLiveMicrophone}}
        >
            {children}
        </TeamsContext.Provider>
    );
};

export function useTeamsContext(): TeamsState {
    const ctx = useContext(TeamsContext);
    if (!ctx) {
        throw new Error('useTeamsContext must be used within a TeamsProvider');
    }
    return ctx;
}
